import "./PreferredBeneficiary.css";
import * as lang from "./lang.json";
import {Input} from "@experta/commons-v2.input";
import {InputRadio} from "@experta/commons-v2.input-radio";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useReducer } from "react";
import PreferredBeneficiaryInsuredForm from "./PreferredBeneficiaryInsuredForm";
import { PreferredBeneficiaryForm, PreferredBeneficiaryProps, CompanyData, getRadioBeneficiaryTypeValues, validateForm } from "./utils";
import { useAppDispatch, useAppSelector } from "../../../Store/hooks";
import { selectBeneficiaryTypes } from "../../../Store/BeneficiaryTypes/Selectors";
import { hideSpinner, showSpinner } from "../../../Store/Spinner/Actions";
import { getBeneficiaryTypesAsync } from "../../../Store/BeneficiaryTypes/Actions";
import { selectPreferredBeneficiaryFormData } from "../../../Store/Forms/Selectors";
import { selectEnvVariables } from "../../../Store/Variables/selectors";

/**
 * Main component.
 * @name PreferredBeneficiary
 * @param {PreferredBeneficiaryProps}
 * @returns {<PreferredBeneficiary />}
 */
const PreferredBeneficiary = ({
  insureds,
  onChangeFormData = (formData: any) => { },
}: PreferredBeneficiaryProps, ref: any) => {

  const dispatch = useAppDispatch();
  const beneficiaryTypes = useAppSelector(selectBeneficiaryTypes);
  const preferredBeneficiariesDataStoreForm = useAppSelector(selectPreferredBeneficiaryFormData);
  const variables = useAppSelector(selectEnvVariables) as any;
  
  const formReducer = (state: PreferredBeneficiaryForm, event: any) => {
    const newState = {
      ...state,
      [event.name]: event.value,
    };
    onChangeFormData(newState);
    return newState;
  };

  const getPreferredBeneficiaryInitialData = useMemo(() => {
    let data = JSON.parse(JSON.stringify(preferredBeneficiariesDataStoreForm));
    if (data.beneficiaryInsures.length === 0) {
      data.beneficiaryInsures = [{ name: '', lastname: '', dni: '', beneficiaryRelationship: '', errorsMessages: [] }];
    }
    return data;
  }, [preferredBeneficiariesDataStoreForm]);

  const [formData, setFormData] = useReducer(formReducer, getPreferredBeneficiaryInitialData);

  const handleChange = (e: any) => {
    if (e.target.value === "JURIDICO") {
      setFormData({
        name: "companyData",
        value: {
          cuit: "",
          businessName: "",
          errorsMessages: []
        },
      });
      setFormData({
        name: "beneficiaryInsures",
        value: [],
      });
    }

    if (e.target.value === "TOMADOR" || e.target.value === lang.defaultPreferredBeneficiaryTypeValue) {
      setFormData({
        name: "beneficiaryInsures",
        value: [],
      });

      setFormData({
        name: "companyData",
        value: { businessName: '', cuit: '', errorsMessages: [] },
      });
    }

    if (e.target.value === "FISICO") {
      setFormData({
        name: "companyData",
        value: { businessName: '', cuit: '', errorsMessages: [] },
      });
      setFormData({
        name: "beneficiaryInsures",
        value: getPreferredBeneficiaryInitialData.beneficiaryInsures,
      });
    }

    setFormData({
      name: e.target.name,
      value: e.target.value,
    });
  };

  const handleChangeCompany = (e: any) => {
    let companyData: CompanyData = { ...formData.companyData, [e.target.name]: e.target.value };

    setFormData({
      name: "companyData",
      value: companyData,
    });
  };

  const handleChangeBeneficiaryInsures = (data: any) => {
    let beneficiaryInsures = [...formData.beneficiaryInsures];
    beneficiaryInsures = data;
    setFormData({
      name: "beneficiaryInsures",
      value: beneficiaryInsures,
    });
  };

  useEffect(() => {
    if (!beneficiaryTypes.length) {
      const getBeneficiaryTypes = async () => {
        await dispatch(showSpinner());
        await dispatch(getBeneficiaryTypesAsync());
        await dispatch(hideSpinner());
      }
      getBeneficiaryTypes();
    }
  }, []);

  useImperativeHandle(ref, () => ({
    validateForm: () => {

      let currentForm: PreferredBeneficiaryForm = validateForm(formData);
      setFormData({ ...currentForm });
      const hasBeneficiariesErrors = currentForm.beneficiaryInsures.filter(a => a.errorsMessages.length > 0).length > 0;
      const hasCompanyDataError = currentForm.companyData.errorsMessages.length > 0;
      if (hasBeneficiariesErrors || hasCompanyDataError) {
        return false;
      }

      return true;
    }
  }), [formData]);


  const hasError = (name: string) => {
    return formData.companyData.errorsMessages.filter(el => el.field === name).length > 0;
  };

  const getError = (name: string) => {
    if (hasError(name)) {
      return formData.companyData.errorsMessages.filter(el => el.field === name)[0].message;
    }
    else {
      return undefined;
    }
  };

  return (
    <div className="preferred-beneficiary-container">
      <div className="preferred-beneficiary-title" style={{ color: variables.REACT_APP_ACCENT_COLOR }}>
        <span>{lang.title}</span>
      </div>

      <div>
        <InputRadio
          onChange={handleChange}
          values={getRadioBeneficiaryTypeValues([{ value: lang.defaultPreferredBeneficiaryTypeValue, label: lang.defaultPreferredBeneficiaryTypeLabel }, ...beneficiaryTypes], formData)}
        />
        {formData.beneficiaryType === "JURIDICO" && (
          <div className="row mt-4">
            <div className="col-md-6 col-12">
              <Input
                name="cuit"
                label={lang.cuit}
                onChange={handleChangeCompany}
                value={formData.companyData.cuit}
                type="text"
                error={hasError("cuit")}
                errorMessage={getError("cuit")}
              />
            </div>
            <div className="col-md-6 col-12">
              <Input
                name="businessName"
                label={lang.businessName}
                onChange={handleChangeCompany}
                value={formData.companyData.businessName}
                type="text"
                error={hasError("businessName")}
                errorMessage={getError("businessName")}
              />
            </div>
          </div>
        )}

        {formData.beneficiaryType === "FISICO" &&
            (
              <PreferredBeneficiaryInsuredForm
                onChange={handleChangeBeneficiaryInsures}
                key={"PreferredBeneficiaryInsuredForm"}
                preferredBeneficiaries={formData.beneficiaryInsures}
              />
            )}
      </div>
    </div>
  );
};

export default forwardRef(PreferredBeneficiary);
